import Script from 'next/script'
import { useEffect } from 'react'

import { getConfig } from '../../../config'
import { GR_ENV } from '../../../constants'
import { useExperimentService, useHasUserClickedOrScrolled, useUserService } from '../../hooks'
import { getSessionStorageItem, SessionStorageKey, setSessionStorageItem } from '../../utils/session-storage'
import { encodeSha256 } from '@lib/utils/crypto'

const { orgId, host, script } = getConfig().tracking.fullStory

const fullStoryScript = `window['_fs_debug'] = false;
window['_fs_host'] = '${host}';
window['_fs_script'] = '${script}';
window['_fs_org'] = '${orgId}';
window['_fs_namespace'] = 'FS';
(function(m,n,e,t,l,o,g,y){
    if (e in m) {if(m.console && m.console.log) { m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].');} return;}
    g=m[e]=function(a,b,s){g.q?g.q.push([a,b,s]):g._api(a,b,s);};g.q=[];
    o=n.createElement(t);o.async=1;o.crossOrigin='anonymous';o.src='https://'+_fs_script;
    y=n.getElementsByTagName(t)[0];y.parentNode.insertBefore(o,y);
    g.identify=function(i,v,s){g(l,{uid:i},s);if(v)g(l,v,s)};g.setUserVars=function(v,s){g(l,v,s)};g.event=function(i,v,s){g('event',{n:i,p:v},s)};
    g.anonymize=function(){g.identify(!!0)};
    g.shutdown=function(){g("rec",!1)};g.restart=function(){g("rec",!0)};
    g.log = function(a,b){g("log",[a,b])};
    g.consent=function(a){g("consent",!arguments.length||a)};
    g.identifyAccount=function(i,v){o='account';v=v||{};v.acctId=i;g(o,v)};
    g.clearUserCookie=function(){};
    g.setVars=function(n, p){g('setVars',[n,p]);};
    g._w={};y='XMLHttpRequest';g._w[y]=m[y];y='fetch';g._w[y]=m[y];
    if(m[y])m[y]=function(){return g._w[y].apply(this,arguments)};
    g._v="1.3.0";
})(window,document,window['_fs_namespace'],'script','user');`

export const FullStoryScript = () => {
  useEffect(() => {
    setSessionStorageItem(SessionStorageKey.persistFullStoryJS, 'true')
  }, [])

  return <Script id="fullstory-inject" type="text/javascript" dangerouslySetInnerHTML={{ __html: fullStoryScript }} />
}

enum FullStoryCustomEvent {
  ExperimentAssignment = 'Experiment Assignment',
}

const trackFullStoryCustomEvent = (eventName: FullStoryCustomEvent, properties: { [p: string]: string }): void => {
  window?.FS?.('trackEvent', {
    name: eventName,
    properties,
  })
}

export const FullStoryScriptLazyInit = () => {
  const hasUserClickedOrScrolled = useHasUserClickedOrScrolled()
  const user = useUserService().currentUser
  const { experimentsWithAssignedTreatment } = useExperimentService()

  useEffect(() => {
    if (hasUserClickedOrScrolled) {
      if (!user.isAnonymous) {
        const getEncodedUserId = async () => {
          return await encodeSha256(user.id)
        }
        getEncodedUserId().then((encodedUserId) => {
          window?.FS?.('setIdentity', { uid: encodedUserId })
          window?.FS?.('setProperties', { type: 'user', properties: { logged_in: true } })
        })
      } else {
        window?.FS?.('setProperties', { type: 'user', properties: { logged_in: false } })
      }

      experimentsWithAssignedTreatment.forEach((treatment, name) => {
        trackFullStoryCustomEvent(FullStoryCustomEvent.ExperimentAssignment, {
          experiment_name: name,
          treatment: treatment.assignedGroup,
        })
      })
    }
  }, [hasUserClickedOrScrolled, experimentsWithAssignedTreatment, user.isAnonymous, user.id])

  return hasUserClickedOrScrolled ? <FullStoryScript /> : null
}

export const shouldEnableFullStory = (isEnabledOnPage: boolean, isGateEnabled: boolean, isGDPR: boolean) => {
  const persistFullStoryJS = getSessionStorageItem(SessionStorageKey.persistFullStoryJS) === 'true'
  return !isGDPR && GR_ENV === 'prod' && isGateEnabled && (isEnabledOnPage || persistFullStoryJS)
}
